import * as React from 'react';

import { cn } from '../../lib/utils';

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Current value, in the same unit as `max`. Omit it for an indeterminate bar
   * (an upload with no known total) — `aria-valuenow` is then left off, which is
   * how ARIA spells "in progress, amount unknown".
   */
  value?: number;
  /** Upper bound of `value`. Defaults to 100, i.e. `value` is a percentage. */
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'destructive';
}

/**
 * Proportion bar with a `progressbar` role.
 *
 * Every call site MUST give the bar an accessible name — `aria-label`, or
 * `aria-labelledby` pointing at the heading it sits under. Without one a screen
 * reader announces a bare percentage with nothing to attach it to.
 *
 * Where a raw percentage is less useful than a phrase, pass `aria-valuetext`
 * ("3 of 12 days used"); it overrides how `aria-valuenow` is read out.
 */
const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value, max = 100, variant = 'default', ...props }, ref) => {
    // Clamped so the announced value can never disagree with the drawn width.
    // A non-finite value or a non-positive max carries no proportion — callers
    // reach that via `0 / 0` on an empty denominator — so the bar reads as
    // indeterminate rather than announcing `aria-valuenow="NaN"`.
    const clampedValue =
      value === undefined || !Number.isFinite(value) || max <= 0
        ? undefined
        : Math.min(max, Math.max(0, value));
    const percentage = clampedValue === undefined ? 0 : (clampedValue / max) * 100;

    const variantClasses = {
      default: 'bg-primary',
      success: 'bg-success',
      warning: 'bg-warning',
      destructive: 'bg-destructive',
    };

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={clampedValue}
        className={cn(
          'relative h-4 w-full overflow-hidden rounded-full bg-secondary/20',
          className
        )}
        {...props}
      >
        <div
          data-slot="progress-fill"
          className={cn(
            'h-full w-full flex-1 transition-all',
            variantClasses[variant]
          )}
          style={{ transform: `translateX(-${100 - percentage}%)` }}
        />
      </div>
    );
  }
);
Progress.displayName = 'Progress';

export { Progress };
